import Vue from 'vue';
import { ActionContext } from 'vuex';

import { IState } from '@app/store';
import { OfferEventCollection } from '@modules/eventGrid/gridUnits/OfferEventCollection';
import StoreUtil from '@store/StoreUtil';

import getters from './getters';
import {
    MarketFiltersState,
    MarketId,
    ModalData,
    TournamentId,
} from './types';

function defaultState(): MarketFiltersState {
    return {
        selectedMarkets: {},
        loading: {},
        initialized: {},
        marketsModalData: null,
        marketsDataForSections: {},
    };
}

const types = {
    SET_SELECTED_MARKET: 'SET_SELECTED_MARKET',
    RESET_SELECTED_MARKETS: 'RESET_SELECTED_MARKETS',
    SET_MARKETS_MODAL_DATA: 'SET_MARKETS_MODAL_DATA',
    SET_LOADING: 'SET_LOADING',
    SET_INITIALIZED: 'SET_INITIALIZED',
    SET_MARKETS_DATA_FOR_SECTIONS: 'SET_MARKETS_DATA_FOR_SECTIONS',
};

const store = {
    namespaced: true,

    state: defaultState(),

    mutations: {
        [types.SET_SELECTED_MARKET](state: MarketFiltersState, payload: { uniqueTournamentId: TournamentId; marketId: MarketId }): void {
            Vue.set(
                state.selectedMarkets,
                payload.uniqueTournamentId,
                payload.marketId,
            );
        },
        [types.RESET_SELECTED_MARKETS](state: MarketFiltersState): void {
            state.selectedMarkets = defaultState().selectedMarkets;
        },
        [types.SET_LOADING](
            state: MarketFiltersState,
            payload: { uniqueTournamentId: TournamentId; isLoading: boolean },
        ): void {
            Vue.set(
                state.loading,
                payload.uniqueTournamentId,
                payload.isLoading,
            );
        },
        [types.SET_INITIALIZED](
            state: MarketFiltersState,
            payload: { uniqueTournamentId: TournamentId; isInitialized: boolean },
        ): void {
            Vue.set(
                state.initialized,
                payload.uniqueTournamentId,
                payload.isInitialized,
            );
        },
        [types.SET_MARKETS_MODAL_DATA](
            state: MarketFiltersState,
            payload: ModalData,
        ): void {
            state.marketsModalData = payload;
        },
        [types.SET_MARKETS_DATA_FOR_SECTIONS](state: MarketFiltersState, marketsDataForSections: MarketFiltersState['marketsDataForSections']): void {
            state.marketsDataForSections = marketsDataForSections;
        },
    },
    getters,
    actions: {
        setSelectedMarket: StoreUtil.createSimpleMutatorAction(types.SET_SELECTED_MARKET),
        setMarketsDataForSections: StoreUtil.createSimpleMutatorAction(types.SET_MARKETS_DATA_FOR_SECTIONS),
        async loadCollectionEvents(
            { dispatch, getters, commit }: ActionContext<MarketFiltersState, IState>,
            { uniqueTournamentId, eventCollections }: { uniqueTournamentId: TournamentId; eventCollections: OfferEventCollection[] },
        ): Promise<void> {
            if (getters.areMarketsInitialized(uniqueTournamentId)) {
                return;
            }
            commit(types.SET_LOADING, { uniqueTournamentId, isLoading: true });
            try {
                const eventIds = eventCollections.flatMap(
                    (eventCollection) => eventCollection.events.map((event) => event.eventId),
                );
                await dispatch('data/sportOffer/getEvents', eventIds, { root: true });
                commit(types.SET_LOADING, { uniqueTournamentId, isLoading: false });
                commit(types.SET_INITIALIZED, { uniqueTournamentId, isInitialized: true });
            } catch {
                commit(types.SET_LOADING, { uniqueTournamentId, isLoading: false });
            }
        },
        openMarketsModal(
            { commit }: ActionContext<MarketFiltersState, IState>,
            modalData: ModalData,
        ): void {
            commit(types.SET_MARKETS_MODAL_DATA, modalData);
        },
        resetSelectedMarkets({ commit }: ActionContext<MarketFiltersState, IState>) {
            commit(types.RESET_SELECTED_MARKETS);
        },
    },
};

export type MarketFiltersStore = typeof store;

export default store;
